<script setup lang="ts">
/**
 * **变参行的一个实例**。
 *
 * ## 为什么要单独一个文件
 *
 * 这一支和「输出点行」长得像、位置又挨着，我在两者之间连错过三次：
 *
 *   1. 改名 UI 加到了**输出行**分支 → 变参行上根本没有改名入口
 *   2. `nodrag` 补到了输出行那份 → dblclick 被图引擎吃掉
 *   3. 输入框的 `text-align` 抄了输出行的 `right`
 *
 * 根因不是粗心，是 1200 行的单文件里**靠缩进认分支**。
 *
 * ## ⚠️ 插槽必须原样转发
 *
 * 宿主通过 `#socket-input` 注入**真实可拖拽的端口**（带 Handle）。
 * 不转发的话画面上端口还在（走默认回退），但**线连不上** ——
 * 而这个错误任何静态检查都不会报。`verify:branches` 会守它。
 */
import { computed } from 'vue'
import NpSocket from '../NpSocket.vue'
import type { RepeaterRow, SocketDef } from '../../types/node'
import type { SocketVisualState } from '../../node/socketState'

const props = defineProps<{
  row: RepeaterRow
  index: number
  total: number
  inGroup: boolean
  state: SocketVisualState
  def: SocketDef
  /** 这一行显示什么名字（用户起的 / 自动跟随上游 / 占位名） */
  label: string
  /** 正在编辑这一行的名字 */
  editing: boolean
  dragging: boolean
  dropTarget: boolean
}>()

/*
 * 变参行有方向：输入端口在行首（左），输出端口在行尾（右）。
 * 落位靠 **DOM 顺序 + flex**（`np-row--out` 靠右），不用绝对定位 ——
 * 这个文件上面记着三条「绝对定位叠在一起」的踩坑。
 */
const isOutput = computed(() => (props.row.side ?? 'input') === 'output')

const emit = defineEmits<{
  (e: 'rename-start'): void
  (e: 'rename-input', value: string): void
  (e: 'rename-cancel'): void
  /** 输入框挂载好了 —— 宿主用来聚焦（组件不自己碰 DOM） */
  (e: 'rename-ready', el: HTMLInputElement | null): void
  (e: 'remove'): void
  (e: 'add'): void
  (e: 'drag-start', ev: PointerEvent): void
  (e: 'drag-move', ev: PointerEvent): void
  (e: 'drag-end', ev: PointerEvent): void
}>()
</script>

<template>
  <div
    class="np-row np-row--repeater nodrag" data-row-kind="repeater"
    :class="{
      'np-row--out': isOutput,
      'np-row--in-group': props.inGroup,
      'is-linked': !!props.state.connected,
      'is-reorderable': !!props.state.connected,
      'is-dragging': props.dragging,
      'is-drop-target': props.dropTarget,
    }"
    @pointerdown="emit('drag-start', $event)"
    @pointermove="emit('drag-move', $event)"
    @pointerup="emit('drag-end', $event)"
    @pointercancel="emit('drag-end', $event)"
    @dblclick.stop="emit('rename-start')"
  >
    <slot v-if="!isOutput" name="socket-input" :def="props.def" :state="props.state" :row="props.row">
      <NpSocket side="input" :def="props.def" v-bind="props.state" />
    </slot>

    <!--
      名字 + 序号：一个 flex:1 的容器。
      序号**紧跟名字**（用户要的），而不是飘到右边。

      ⚠️ 名字按钮上**不能** `@pointerdown.stop` —— 那会挡住整行的拖拽排序
      （拖拽热区就是整行）。这里只 `@click.stop` 就够：
      点一下不动 = 改名，移动超过 5px = 排序，两者不冲突。
    -->
    <div class="np-row__name-group">
      <input
        v-if="props.editing"
        :ref="(el) => emit('rename-ready', el as HTMLInputElement | null)"
        class="np-row__label np-row__rename nodrag"
        :value="props.label"
        spellcheck="false"
        @pointerdown.stop
        @click.stop
        @input="emit('rename-input', ($event.target as HTMLInputElement).value)"
        @keydown.enter.prevent="emit('rename-cancel')"
        @keydown.escape.prevent="emit('rename-cancel')"
        @blur="emit('rename-cancel')"
      />
      <button
        v-else
        class="np-row__label np-row__label--rename"
        type="button"
        title="点击改名；按住拖动可以排序"
        @click.stop="emit('rename-start')"
      >
        {{ props.label }}
      </button>

      <span v-if="props.total > 1" class="np-row__repeater-index">
        {{ props.index + 1 }}
      </span>
    </div>

    <!--
      右边的操作区。**全部走正常流式布局**，不用绝对定位 ——
      之前 ✕ 是 `position: absolute; right: …`，和抓手叠在一起了。

      ⚠️ 改名按钮原来还挂着一个 `--edit` 修饰类，**它没有任何样式**
      （通用 `.np-row__icon` 已经够了）—— 已删，别再加回来。
    -->
    <button
      class="np-row__icon nodrag"
      type="button"
      title="改名"
      @pointerdown.stop
      @click.stop="emit('rename-start')"
    >
      <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path
          d="M2 10 L2.6 7.6 L8.4 1.8 L10.2 3.6 L4.4 9.4 Z"
          stroke="currentColor"
          stroke-width="1.2"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <!-- 抓手：只提示可拖，真正的拖拽热区是整行 -->
    <span v-if="props.state.connected" class="np-row__grip" aria-hidden="true">
      <svg viewBox="0 0 8 12" fill="currentColor">
        <circle cx="2" cy="2" r="1" />
        <circle cx="6" cy="2" r="1" />
        <circle cx="2" cy="6" r="1" />
        <circle cx="6" cy="6" r="1" />
        <circle cx="2" cy="10" r="1" />
        <circle cx="6" cy="10" r="1" />
      </svg>
    </span>

    <!--
      删除：**只在已连接的实例上出现**。
      未连接的那个（永远是最后一个空槽）没有东西可删 ——
      非最后的实例在断开连线时会自动压紧掉。
    -->
    <button
      v-if="props.state.connected"
      class="np-row__icon np-row__icon--remove nodrag"
      type="button"
      :title="`删除第 ${props.index + 1} 项（连同它的连线）`"
      @pointerdown.stop
      @click.stop="emit('remove')"
    >
      <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
        <path
          d="M2.6 2.6 L7.4 7.4 M7.4 2.6 L2.6 7.4"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
        />
      </svg>
    </button>

    <slot
      v-if="isOutput"
      name="socket-output"
      :def="props.def"
      :state="props.state"
      :row="props.row"
    >
      <NpSocket
        side="output"
        :type="props.def.type"
        :data-socket-id="props.def.id"
        v-bind="props.state"
      />
    </slot>
  </div>
</template>
